import React, { useEffect, useState, useCallback, useRef } from "react";
import { Dialog, Button, confirmDialog } from "@/components/ui";
import { SmartLink, SmartLinkExtraParam } from "@/services/bedrock/dtos";
import { PlayerCardServices, SmartLinkServices } from "@/services/bedrock";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { ArrowLeft, Trash2Icon } from "lucide-react";
import { useDebounced, useSmartLinkSearch } from "@/utils";
import { cn } from "@/lib/utils";

import { SmartLinkDecisionView, SmartLinkListView, SmartLinkUrlStepView, SmartLinkFormView } from "./components";

interface SmartLinkDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  smartLink?: SmartLink;
  altThumbnail?: string;
  onSuccess: (smartLink: SmartLink) => void;
  onCancel?: () => void;
  initialMode?: Mode;
}

const smartLinkSchema = z.object({
  name: z.string().min(1, "Name is required").max(120, "Name must be less than 120 characters"),
  destinationUrl: z.url("Invalid URL").min(1, "Destination URL is required"),
  ogTitle: z.string().optional(),
  ogDescription: z.string().optional(),
  ogImageUrl: z.string().optional(),
  stripExistingUtms: z.boolean(),
});

type SmartLinkFormValues = z.infer<typeof smartLinkSchema>;

export type Mode = "DECISION" | "LIST" | "FORM" | "CREATE_URL_STEP";

const UTM_PARAMS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"];

export const SmartLinkDialog: React.FC<SmartLinkDialogProps> = ({
  open,
  onOpenChange,
  smartLink,
  altThumbnail,
  onSuccess,
  onCancel,
  initialMode,
}) => {
  const [mode, setMode] = useState<Mode>("LIST");
  const [editingSmartLink, setEditingSmartLink] = useState<SmartLink | undefined>(smartLink);
  const [pendingExtraParams, setPendingExtraParams] = useState<Partial<SmartLinkExtraParam>[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebounced(searchTerm, 500);
  const [isFetchingMetadata, setIsFetchingMetadata] = useState(false);
  const lastFetchedUrlRef = useRef<string | undefined>();

  const {
    data: linksData,
    createSmartLink,
    isCreating,
    updateSmartLink,
    isUpdating,
  } = useSmartLinkSearch({
    enabled: open && !smartLink && !initialMode,
    isActive: true,
    perPage: 1,
  });
  const isSubmitting = isCreating || isUpdating;

  const form = useForm<SmartLinkFormValues>({
    resolver: zodResolver(smartLinkSchema),
    mode: "onTouched",
    defaultValues: {
      name: "",
      destinationUrl: "",
      ogTitle: "",
      ogDescription: "",
      ogImageUrl: "",
      stripExistingUtms: true,
    },
  });

  const destinationUrl = form.watch("destinationUrl");

  const hasExistingUtms = React.useMemo(() => {
    try {
      const url = new URL(destinationUrl);
      return UTM_PARAMS.some(param => url.searchParams.has(param));
    } catch {
      return false;
    }
  }, [destinationUrl]);

  const stripUtms = (urlStr: string) => {
    try {
      const url = new URL(urlStr);
      UTM_PARAMS.forEach(param => url.searchParams.delete(param));
      return url.toString();
    } catch {
      return urlStr;
    }
  };

  const fetchMetadata = useCallback(
    async (url: string, shouldUpdateName = false) => {
      if (!url) return;
      try {
        new URL(url); // Validate URL format
      } catch (e) {
        return;
      }

      setIsFetchingMetadata(true);
      lastFetchedUrlRef.current = url;
      try {
        const metadata = await PlayerCardServices.fetchUrlMetadata(url);
        const title = metadata.title || metadata.ogTitle || "";
        form.setValue("ogTitle", title);
        form.setValue("ogDescription", metadata.description || metadata.ogDescription || "");

        const imageUrl = metadata.image?.url || metadata.ogImage || altThumbnail || metadata.logo?.url || "";
        form.setValue("ogImageUrl", imageUrl);

        if (shouldUpdateName && title) {
          form.setValue("name", title);
          const invalidNameError = smartLinkSchema.shape.name.safeParse(title).error?.issues[0].message;
          if (invalidNameError) {
            form.setError("name", { message: invalidNameError });
          }
        }
      } catch (error) {
        console.error("Failed to fetch metadata", error);
      } finally {
        setIsFetchingMetadata(false);
      }
    },
    [form]
  );

  // Debounced effect for automatic fetching (only for FORM mode)
  useEffect(() => {
    const timer = setTimeout(() => {
      const isValid = smartLinkSchema.shape.destinationUrl.safeParse(destinationUrl).success;
      if (isValid && mode === "FORM" && destinationUrl !== lastFetchedUrlRef.current) {
        fetchMetadata(destinationUrl);
      }
    }, 1000);
    return () => clearTimeout(timer);
  }, [destinationUrl, fetchMetadata, mode]);

  useEffect(() => {
    if (open) {
      if (smartLink) {
        setMode(initialMode || "DECISION");
        setEditingSmartLink(smartLink);
        setPendingExtraParams([]);
        const initialValues = {
          name: smartLink.name,
          destinationUrl: smartLink.destinationUrl,
          ogTitle: smartLink.ogTitle || "",
          ogDescription: smartLink.ogDescription || "",
          ogImageUrl: smartLink.ogImageUrl || "",
          stripExistingUtms: true,
        };
        form.reset(initialValues);
        lastFetchedUrlRef.current = initialValues.destinationUrl;
      } else {
        const hasNoLinks = linksData && linksData.pagination.totalCount === 0;
        setMode(initialMode || (hasNoLinks ? "CREATE_URL_STEP" : "LIST"));
        setEditingSmartLink(undefined);
        setPendingExtraParams([]);
        form.reset({
          name: "",
          destinationUrl: "",
          ogTitle: "",
          ogDescription: "",
          ogImageUrl: "",
          stripExistingUtms: true,
        });
        lastFetchedUrlRef.current = "";
      }
    }
  }, [open, smartLink, form, initialMode, linksData]);

  const onSubmit = async (data: SmartLinkFormValues) => {
    try {
      const { stripExistingUtms, ...serviceData } = data;
      let finalUrl = data.destinationUrl;
      if (stripExistingUtms) {
        finalUrl = stripUtms(finalUrl);
      }

      let result: SmartLink;
      if (editingSmartLink) {
        result = await updateSmartLink({
          id: editingSmartLink.id,
          data: {
            ...serviceData,
            destinationUrl: finalUrl,
          },
        });
      } else {
        result = await createSmartLink({
          ...serviceData,
          destinationUrl: finalUrl,
        });

        if (pendingExtraParams.length > 0) {
          try {
            await Promise.all(
              pendingExtraParams.map(param =>
                SmartLinkServices.createSmartLinkExtraParam({
                  queryKey: param.queryKey!,
                  valueType: param.valueType!,
                  value: param.value,
                  smartLinkId: result.id,
                })
              )
            );
          } catch (e) {
            console.error("Failed to save pending extra parameters", e);
          }
        }
      }
      onSuccess(result);
      onOpenChange(false);
    } catch (error) {
      // Error is handled by mutation toast
    }
  };

  const handleUrlStepSubmit = async () => {
    const isValid = await form.trigger("destinationUrl");
    if (isValid) {
      // 1. Fetch metadata first
      await fetchMetadata(destinationUrl, true);

      // 2. Default name if missing
      if (!editingSmartLink) {
        const currentValues = form.getValues();
        form.setValue("name", currentValues.name || currentValues.ogTitle || "New Smart Link");
      }

      setMode("FORM");
    }
  };

  const handleArchive = async () => {
    if (!editingSmartLink) return;

    const confirmed = await confirmDialog({
      title: "Archive Smart Link?",
      description: (
        <div className="flex flex-col gap-3">
          <p>
            Are you sure you want to archive <strong>{editingSmartLink.name}</strong>?
          </p>
          <div className="flex flex-col gap-2 p-3 bg-amber-50 border border-amber-100 rounded-lg text-sm text-amber-800 leading-relaxed font-sans">
            <span className="font-bold block mb-1">Impact on Prompts & Posts</span>
            <div>
              All Prompts assigned to this Smart Link will revert to the player's primary short link. Any
              already-published posts will also automatically redirect there to prevent broken links.
            </div>
          </div>
        </div>
      ),
      confirmLabel: "Archive Link",
      cancelLabel: "Keep Link",
      destructive: true,
      size: "md",
    });

    if (confirmed) {
      try {
        const result = await updateSmartLink({
          id: editingSmartLink.id,
          data: { isActive: false },
        });
        onSuccess(result);
        onOpenChange(false);
      } catch (error) {
        // Error is handled by mutation toast
      }
    }
  };

  const handleCancel = useCallback(() => {
    onOpenChange(false);
    onCancel?.();
  }, [onOpenChange, onCancel]);

  const handleBack = useCallback(() => {
    if (mode === initialMode) {
      handleCancel();
      return;
    }

    if (mode === "LIST") {
      if (smartLink) setMode("DECISION");
      else handleCancel();
    } else if (mode === "CREATE_URL_STEP") {
      if (smartLink) setMode("DECISION");
      else setMode("LIST");
    } else if (mode === "FORM") {
      if (editingSmartLink && editingSmartLink.id) {
        if (editingSmartLink.id === smartLink?.id) setMode("DECISION");
        else setMode("LIST");
      } else {
        setMode("CREATE_URL_STEP");
      }
    } else if (mode === "DECISION") {
      handleCancel();
    }
  }, [mode, initialMode, smartLink, editingSmartLink, handleCancel]);

  const isGlobalEdit = editingSmartLink && !!editingSmartLink.id;

  return (
    <Dialog open={open} onOpenChange={onOpenChange} size={mode === "FORM" ? "2xl" : "md"}>
      <Dialog.Header>
        <div className="flex items-center gap-2">
          {mode !== initialMode && (
            <>
              {mode === "DECISION" && initialMode === "DECISION" && (
                <Button variant="ghost" size="icon" className="size-8" type="button" onClick={handleBack}>
                  <ArrowLeft className="size-4" />
                </Button>
              )}
              {mode === "LIST" && (smartLink || initialMode === "LIST") && (
                <Button variant="ghost" size="icon" className="size-8" type="button" onClick={handleBack}>
                  <ArrowLeft className="size-4" />
                </Button>
              )}
              {mode === "CREATE_URL_STEP" && (
                <Button variant="ghost" size="icon" className="size-8" type="button" onClick={handleBack}>
                  <ArrowLeft className="size-4" />
                </Button>
              )}
              {mode === "FORM" && (
                <Button variant="ghost" size="icon" className="size-8" type="button" onClick={handleBack}>
                  <ArrowLeft className="size-4" />
                </Button>
              )}
            </>
          )}
          <Dialog.Title>
            {mode === "DECISION"
              ? "Manage Smart Link"
              : mode === "LIST"
              ? "Select Smart Link"
              : editingSmartLink
              ? "Edit Smart Link"
              : "Create Smart Link"}
          </Dialog.Title>
        </div>
        <Dialog.Description>
          {mode === "DECISION"
            ? "You are currently using a smart link. What would you like to do?"
            : mode === "LIST"
            ? "Choose a trackable link for your post or create a new one."
            : editingSmartLink
            ? "Update your smart link details below."
            : "Enter the details for your new smart link."}
        </Dialog.Description>
      </Dialog.Header>

      <Dialog.Content className="max-h-[calc(100vh-14rem)] overflow-y-auto no-scrollbar">
        {mode === "DECISION" && (
          <SmartLinkDecisionView
            smartLink={editingSmartLink!}
            onEditGlobally={() => {
              lastFetchedUrlRef.current = form.getValues("destinationUrl");
              setMode("FORM");
            }}
            onChooseDifferent={() => setMode("LIST")}
          />
        )}
        {mode === "LIST" && (
          <SmartLinkListView
            searchQuery={searchTerm}
            onSearchChange={setSearchTerm}
            debouncedSearch={debouncedSearch}
            onSelect={link => {
              onSuccess(link);
              onOpenChange(false);
            }}
            onEdit={link => {
              setEditingSmartLink(link);
              setPendingExtraParams([]);
              const initialValues = {
                name: link.name,
                destinationUrl: link.destinationUrl,
                ogTitle: link.ogTitle || "",
                ogDescription: link.ogDescription || "",
                ogImageUrl: link.ogImageUrl || "",
                stripExistingUtms: true,
              };
              form.reset(initialValues);
              lastFetchedUrlRef.current = initialValues.destinationUrl;
              setMode("FORM");
            }}
            onCreateNew={() => {
              setEditingSmartLink(undefined);
              setPendingExtraParams([]);
              const initialValues = {
                name: "",
                destinationUrl: "",
                ogTitle: "",
                ogDescription: "",
                ogImageUrl: "",
                stripExistingUtms: true,
              };
              form.reset(initialValues);
              lastFetchedUrlRef.current = "";
              setMode("CREATE_URL_STEP");
            }}
          />
        )}
        {mode === "CREATE_URL_STEP" && (
          <SmartLinkUrlStepView
            control={form.control}
            isFetchingMetadata={isFetchingMetadata}
            onSubmit={handleUrlStepSubmit}
            destinationUrl={destinationUrl}
          />
        )}
        {mode === "FORM" && (
          <form id="smart-link-form" onSubmit={form.handleSubmit(onSubmit)}>
            <SmartLinkFormView
              form={form}
              isSubmitting={isSubmitting}
              isFetchingMetadata={isFetchingMetadata}
              onRefreshMetadata={() => fetchMetadata(destinationUrl)}
              isGlobalEdit={isGlobalEdit!!}
              hasExistingUtms={hasExistingUtms}
              destinationUrl={destinationUrl}
              smartLinkId={editingSmartLink?.id}
              isCreatingNewLink={!editingSmartLink}
              pendingParams={pendingExtraParams}
              onPendingParamsChange={setPendingExtraParams}
            />
          </form>
        )}
      </Dialog.Content>

      {mode === "FORM" && (
        <Dialog.Footer>
          <div className="flex items-center justify-between gap-2 w-full">
            <div className={"flex flex-row gap-2"}>
              {editingSmartLink?.id && (
                <Button variant={"destructive"} onClick={handleArchive}>
                  <Trash2Icon />
                  Archive
                </Button>
              )}
            </div>
            <div className="flex flex-row gap-2">
              <Button type="button" variant="outline" onClick={handleBack} disabled={isSubmitting}>
                Back
              </Button>
              <Button
                type="submit"
                form="smart-link-form"
                disabled={isSubmitting || isFetchingMetadata}
                loading={isSubmitting}
                className={cn(isGlobalEdit ? "bg-amber-600 hover:bg-amber-700 text-white" : "")}
              >
                {editingSmartLink ? "Save Changes" : "Create Smart Link"}
              </Button>
            </div>
          </div>
        </Dialog.Footer>
      )}
    </Dialog>
  );
};
